import { Container } from '../../odie';

import { app } from '../app';
import { IAnimation } from '../../lib/pattern/IAnimation';
import { ParticleEmitter } from '../particles/ParticleEmitter';
import { getMatch2AssetPath } from '../util/assets';
import { tweenPower4Out } from '../../lib/util/tweens';
import { DynamicNumber, SizeType } from '../../lib/defs/types';

// types
//-----------------------------------------------------------------------------
export type SmokeAnimationOptions = {
  size: SizeType;
  amount: number;
  tint: number;
  duration: number;
  magnitude: DynamicNumber;
  startScale: number;
  endScale: number;
};

const defaultOptions: SmokeAnimationOptions = {
  size: {},
  amount: 3,
  tint: 0xffffff,
  duration: 1,
  magnitude: [80, 100],
  startScale: 1,
  endScale: 1.5,
};

/*
    smoke animation
*/
export class SmokeAnimation extends Container implements IAnimation {
  // fields
  //-------------------------------------------------------------------------
  private _options: SmokeAnimationOptions;

  // init
  //-------------------------------------------------------------------------
  constructor(options?: Partial<SmokeAnimationOptions>) {
    super();
    this._options = {
      ...defaultOptions,
      ...options,
    };
    this.scaleX = this._options.size.width ?? 1;
    this.scaleY = this._options.size.height ?? 1;
  }

  public static assets(): string[] {
    return ['fx.basic'];
  }

  // impl
  //-------------------------------------------------------------------------
  public async start(): Promise<void> {
    // create smoke
    const smokeEmitter = this._createSmokeEmitter();

    // animate smoke
    await this._animateSmoke(smokeEmitter);
  }

  public stop() {}

  // private: animators
  //-------------------------------------------------------------------------
  private async _animateSmoke(smoke: ParticleEmitter) {
    // spawn smoke
    this.addChild(smoke.view);

    // start
    await smoke.start();

    // despawn smoke
    this.removeChild(smoke.view);
  }

  // private: factory
  //-------------------------------------------------------------------------
  private _createSmokeEmitter(): ParticleEmitter {
    const { amount, tint, duration, magnitude, startScale, endScale } =
      this._options;
    return app.particles.create({
      textures: [getMatch2AssetPath('fx.basic/fx.basic.smoke.png')],
      rate: 0,
      limit: amount,
      emitterDuration: 0,
      duration,
      tint,
      scale: { x: startScale, y: startScale },
      behaviors: [
        {
          type: 'explode',
          magnitude,
        },
        { type: 'scale', to: endScale, tween: tweenPower4Out },
        { type: 'fade', to: 0, tween: tweenPower4Out },
        { type: 'kinematic' },
      ],
    });
  }
}
